import type { LocalRow, SeedTables } from '../types/localDb.types'
import { roleId, seedAudit } from './platform.seed'
import { SEED_GROUP, seedId } from './seedId'

/**
 * Konfigurasi Workflow Studio WF-TOB-01 (Penerimaan Tembakau) — disalin dari
 * `docs/tl-doc/12B-SERVICE-DATA/JAL_12B-SERVICE-DATA-WF-TOB-01.md` §1–§8 (label [C]).
 * Tabel email, tabbing, dan SLA tidak di-seed karena tidak dijalankan local mode.
 */

const WORKFLOW_NO = 2
export const WF_TOB_01_ID = seedId(SEED_GROUP.workflow, WORKFLOW_NO)

/** Rentang id per workflow dipisah ribuan agar tidak bertabrakan dengan WF-PRC-01. */
const scoped = (n: number) => WORKFLOW_NO * 1000 + n

const STATUSES = [
  ['TOB_REC_DRAFT', 'Draft', 1, true, false, '#64748B', 'Draft penerimaan tembakau dicatat Admin RM'],
  ['TOB_REC_PENDING_SPV', 'Menunggu Approval', 2, false, false, '#F59E0B', 'Menunggu persetujuan Supervisor RM'],
  ['TOB_REC_APPROVED', 'Disetujui', 3, false, false, '#10B981', 'Disetujui, QR batch diterbitkan, siap putaway'],
  ['TOB_REC_PUTAWAY', 'In Storage', 4, false, true, '#059669', 'Semua tumpukan tembakau telah ditaruh di rak'],
  ['TOB_REC_REJECTED', 'Ditolak', 5, false, true, '#EF4444', 'Penerimaan ditolak Supervisor RM']
] as const

export type TobStatusCode = (typeof STATUSES)[number][0]

export const tobStatusId = (code: TobStatusCode) =>
  seedId(SEED_GROUP.wmlStatus, scoped(STATUSES.findIndex(([c]) => c === code) + 1))

const ACTORS = [
  ['ACT_TOB_ADMIN_RM', 'Admin WH RM', 'admin_wh_rm', { created_by: { _eq: '$CURRENT_USER.id' } }],
  ['ACT_TOB_SPV_RM', 'WH RM Supervisor', 'wh_rm_supervisor', { created_by: { _neq: '$CURRENT_USER.id' } }],
  ['ACT_TOB_OP_RM', 'Operator WH RM', 'operator_wh_rm', null]
] as const

export type TobActorCode = (typeof ACTORS)[number][0]

export const tobActorId = (code: TobActorCode) =>
  seedId(SEED_GROUP.wmlActor, scoped(ACTORS.findIndex(([c]) => c === code) + 1))

/** ui_config: TL 12B §5 hanya mendefinisikan CMP_TOB_REC_SUBMIT; komponen lain [A] mengikuti pola WF-PRC-01. */
const COMPONENTS = [
  ['CMP_TOB_REC_SUBMIT', 'Tombol Ajukan ke Supervisor', 'ACTION', { color: '#3B82F6', variant: 'primary' }],
  ['CMP_TOB_REC_APP_SPV', 'Tombol Approve Penerimaan', 'ACTION', { color: '#3B82F6', icon: 'tabler:check' }],
  ['CMP_TOB_REC_REJ_SPV', 'Tombol Reject Penerimaan', 'ACTION', { color: '#EF4444', icon: 'tabler:x' }],
  ['CMP_TOB_REC_FINISH_PUT', 'Tombol Selesai Putaway Tembakau', 'ACTION', { color: '#3B82F6' }],
  ['CMP_TOB_REC_PANEL_HEADER', 'Panel Header Penerimaan', 'PANEL', {}],
  ['CMP_TOB_REC_PANEL_ITEMS', 'Panel Rincian Sak & Grade', 'PANEL', {}]
] as const

type ComponentCode = (typeof COMPONENTS)[number][0]

const componentId = (code: ComponentCode) =>
  seedId(SEED_GROUP.wmlComponent, scoped(COMPONENTS.findIndex(([c]) => c === code) + 1))

/** [name, from, to, component, is_need_reason, is_reason_required, entity_patch_mapping] — TL 12B §6. */
const ACTIONS: [
  string,
  TobStatusCode,
  TobStatusCode,
  ComponentCode,
  boolean,
  boolean,
  Record<string, unknown> | null
][] = [
  ['ACT_TOB_REC_SEND', 'TOB_REC_DRAFT', 'TOB_REC_PENDING_SPV', 'CMP_TOB_REC_SUBMIT', false, false, null],
  ['ACT_TOB_REC_APP', 'TOB_REC_PENDING_SPV', 'TOB_REC_APPROVED', 'CMP_TOB_REC_APP_SPV', false, false, null],
  ['ACT_TOB_REC_REJ', 'TOB_REC_PENDING_SPV', 'TOB_REC_REJECTED', 'CMP_TOB_REC_REJ_SPV', true, true, null],
  [
    'ACT_TOB_REC_STORE',
    'TOB_REC_APPROVED',
    'TOB_REC_PUTAWAY',
    'CMP_TOB_REC_FINISH_PUT',
    false,
    false,
    { is_stored: true, stored_at: '$NOW' }
  ]
]

export const tobActionId = (name: string) =>
  seedId(SEED_GROUP.wmlAction, scoped(ACTIONS.findIndex(([n]) => n === name) + 1))

const ELIGIBILITY: [string, TobActorCode][] = [
  ['ACT_TOB_REC_SEND', 'ACT_TOB_ADMIN_RM'],
  ['ACT_TOB_REC_APP', 'ACT_TOB_SPV_RM'],
  ['ACT_TOB_REC_REJ', 'ACT_TOB_SPV_RM'],
  ['ACT_TOB_REC_STORE', 'ACT_TOB_OP_RM']
]

/**
 * [komponen panel, status, permission, actor]. TL 12B §8 hanya mengisi actor untuk status DRAFT;
 * [A] panel read pada status lain diberikan ke semua actor WF-TOB-01, termasuk APPROVED & REJECTED
 * yang tidak tercantum di TL.
 */
const PANELS: [ComponentCode, TobStatusCode, 'read' | 'write', TobActorCode | null][] = [
  ['CMP_TOB_REC_PANEL_HEADER', 'TOB_REC_DRAFT', 'write', 'ACT_TOB_ADMIN_RM'],
  ['CMP_TOB_REC_PANEL_HEADER', 'TOB_REC_PENDING_SPV', 'read', null],
  ['CMP_TOB_REC_PANEL_HEADER', 'TOB_REC_APPROVED', 'read', null],
  ['CMP_TOB_REC_PANEL_HEADER', 'TOB_REC_PUTAWAY', 'read', null],
  ['CMP_TOB_REC_PANEL_HEADER', 'TOB_REC_REJECTED', 'read', null],
  ['CMP_TOB_REC_PANEL_ITEMS', 'TOB_REC_DRAFT', 'write', 'ACT_TOB_ADMIN_RM'],
  ['CMP_TOB_REC_PANEL_ITEMS', 'TOB_REC_PENDING_SPV', 'read', null],
  ['CMP_TOB_REC_PANEL_ITEMS', 'TOB_REC_APPROVED', 'read', null],
  ['CMP_TOB_REC_PANEL_ITEMS', 'TOB_REC_PUTAWAY', 'read', null],
  ['CMP_TOB_REC_PANEL_ITEMS', 'TOB_REC_REJECTED', 'read', null]
]

export function buildWorkflowTobSeed(): SeedTables {
  const panels: LocalRow[] = PANELS.map(([component, status, permission], index) => ({
    ...seedAudit,
    id: seedId(SEED_GROUP.wmlPanel, scoped(index + 1)),
    component_id: componentId(component),
    status_id: tobStatusId(status),
    permission
  }))

  return {
    wml_workflow: [
      {
        ...seedAudit,
        id: WF_TOB_01_ID,
        code: 'WF-TOB-01',
        name: 'Tobacco RM Receipt Workflow',
        entity: 'rm_tobacco_receipt',
        source_policy: 'manual_create'
      }
    ],
    wml_status: STATUSES.map(([code, name, order, isStart, isTerminal, color, desc], index) => ({
      ...seedAudit,
      id: seedId(SEED_GROUP.wmlStatus, scoped(index + 1)),
      workflow_id: WF_TOB_01_ID,
      code,
      name,
      order,
      is_start: isStart,
      is_terminal: isTerminal,
      is_send_email: false,
      color,
      desc
    })),
    wml_actor: ACTORS.map(([code, name, role, condition]) => ({
      ...seedAudit,
      id: tobActorId(code),
      workflow_id: WF_TOB_01_ID,
      code,
      name,
      role_id: roleId(role),
      job_fn_id: null,
      condition
    })),
    wml_component: COMPONENTS.map(([code, name, type, uiConfig]) => ({
      ...seedAudit,
      id: componentId(code),
      workflow_id: WF_TOB_01_ID,
      code,
      name,
      type,
      ui_config: uiConfig
    })),
    wml_action: ACTIONS.map(([name, from, to, component, needReason, reasonRequired, patch]) => ({
      ...seedAudit,
      id: tobActionId(name),
      workflow_id: WF_TOB_01_ID,
      name,
      from_status_id: tobStatusId(from),
      to_status_id: tobStatusId(to),
      component_id: componentId(component),
      is_need_reason: needReason,
      is_reason_required: reasonRequired,
      is_post_closure: false,
      entity_patch_mapping: patch
    })),
    wml_action_actor_eligibility: ELIGIBILITY.map(([action, actor], index) => ({
      ...seedAudit,
      id: seedId(SEED_GROUP.wmlActionActor, scoped(index + 1)),
      action_id: tobActionId(action),
      actor_id: tobActorId(actor)
    })),
    wml_panel: panels,
    wml_panel_actor_eligibility: PANELS.flatMap(([, , , actor], index) =>
      (actor ? [actor] : ACTORS.map(([code]) => code)).map((actorCode, offset) => ({
        ...seedAudit,
        id: seedId(SEED_GROUP.wmlPanelActor, scoped((index + 1) * 10 + offset)),
        panel_id: panels[index].id,
        actor_id: tobActorId(actorCode)
      }))
    )
  }
}
